<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" />
      <p class="mb-4">{{ $t('route.routePlaning.textDetail') }}</p>

      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end">
          <b-button @click="$router.go(-1)" variant="outline-primary">
            <i class="mc-fa icon-outline_arrow-left"></i> {{ $t('global.back') }}
          </b-button>
        </div>
      </div>
    </div>

    <div class="row mb-4">
      <div class="col-md-8">
        <div class="mc-table-filter__status pb-4">
          <b-button
            @click="filterByStatus('scheduled')"
            :variant="status === 'scheduled' ? 'primary' : 'outline-primary'"
            size="sm"
            class="me-2"
          >
            {{ $t('master.status.dijadwalkan') }}</b-button
          >
          <b-button
            @click="filterByStatus('progress')"
            :variant="status === 'progress' ? 'primary' : 'outline-primary'"
            size="sm"
            class="me-2"
          >
            {{ $t('master.status.dalamPerjalanan') }}</b-button
          >
          <b-button
            @click="filterByStatus('done')"
            :variant="status === 'done' ? 'primary' : 'outline-primary'"
            size="sm"
            class="me-2"
          >
            {{ $t('master.status.selesai') }}</b-button
          >
          <b-button
            @click="filterByStatus('cancelled')"
            :variant="status === 'cancelled' ? 'primary' : 'outline-primary'"
            size="sm"
            class="me-2"
          >
            {{ $t('master.status.dibatalkan') }}</b-button
          >
        </div>
      </div>
    </div>
    <McEmptyState
      v-if="empty"
      title="Belum ada data kendaraan"
      desc="Tambahkan data terlebih dulu ya!"
      @buttonPrimaryAction="customHandlers.handleAdd()"
    ></McEmptyState>

    <McGrapqlTable
      v-else
      :apiClient="query"
      :fields="fields"
      :customHandlers="customHandlers"
      :hasCheckbox="hasCheckbox"
      :searchable="true"
      :withCard="true"
      :hasDelete="false"
      :hasSelectAll="false"
      :isSelectableField="true"
      :refreshData="refreshData"
      trackBy="routeRecordId"
      ref="tablePlanning"
      checkType="single"
      @totalSelected="updateTotal"
    >
      <template #button-header>
        <b-dropdown
          toggle-tag="div"
          toggle-class="p-0 bg-transparent me-2 border-none"
          :disabled="!changeStatusList"
        >
          <template #button-content>
            <McButton
              variant="secondary"
              :text="$t('global.action.changeStatus')"
              :icon="'icon-outline_chevron-down'"
              icon-position="right"
            />
          </template>
          <b-dropdown-item
            v-for="statusList in changeStatusList"
            :key="statusList.id"
            @click="openModalChangeStatus(statusList)"
            >{{ statusList.label }}</b-dropdown-item
          >
        </b-dropdown>
      </template>
      <template #cell(status)="data">
        <span class="mc-status mc-status--green" v-if="data.value === 'done'">
          {{ $t('master.status.selesai') }}</span
        >
        <span class="mc-status mc-status--red" v-else-if="data.value === 'cancelled'">
          {{ $t('master.status.dibatalkan') }}</span
        >
        <span class="mc-status mc-status--blue" v-else-if="data.value === 'progress'">
          {{ $t('master.status.dalamPerjalanan') }}</span
        >
        <span class="mc-status mc-status--grey" v-else-if="data.value === 'scheduled'">
          {{ $t('master.status.dijadwalkan') }}</span
        >
      </template>

      <template #cell(expectedStartOn)="data">
        <p>{{ momentDateShortMonth(data.value) }}</p>
        <span class="text-muted">{{ `${momentTime(data.value)} WIB` }}</span>
      </template>

      <template #cell(expectedEndOn)="data">
        <template v-if="data.value">
          <p>{{ momentDateShortMonth(data.value) }}</p>
          <span class="text-muted">{{ `${momentTime(data.value)} WIB` }}</span>
        </template>
        <template v-else>
          <span>-</span>
        </template>
      </template>
    </McGrapqlTable>

    <b-modal
      class="mc-modal"
      id="mc-modal-change-status"
      ref="mc-modal-change-status"
      :title="$t('global.action.changeStatus')"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p>
          {{ $t('global.text.modal.changeStatus') }}
          <strong>{{ selectedIdsLength }}</strong> {{ $t('global.text.modal.changeStatusLength') }}
          {{ statusChanged.label }} ?
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            :text="$t('global.action.cancel')"
            class="me-2"
            @click="cancel()"
          />
          <McButton variant="primary" :text="$t('global.action.change')" @click="changeStatus" />
        </div>
      </template>
    </b-modal>
  </Layout>
</template>
<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import McGrapqlTable from '@/components/McGrapqlTable'
import McEmptyState from '@/components/McEmptyState/index'
import McButton from '@/components/McButton'
import { GET_ROUTE_RECORDS, UPDATE_STATUS_ROUTE_RECORD } from '@/services/api/route'
import { Helper } from '@/mixin/helper'
import { MixPanel } from '@/mixin/mixpanel'

/**
 * Starter component
 */
export default {
  page: {
    title: 'Route',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McEmptyState,
    McGrapqlTable,
    McButton,
  },

  mixins: [Helper, MixPanel],

  data() {
    return {
      empty: false,
      refreshData: false,
      hasCheckbox: true,
      status: 'scheduled',
      statusChanged: '',
      filter: {},
      selectedIdsLength: 0,

      customHandlers: {
        handleAdd: () => {
          return this.$router.push({ name: 'Route Planning Form' })
        },
        handleClickRow: (item) => {
          return this.$router.push({
            name: 'Route Planning Form',
            params: { routeRecordId: item.routeRecordId },
          })
        },
      },
    }
  },

  computed: {
    title() {
      return this.$t('route.routePlaning.title')
    },

    fields() {
      return [
        {
          key: 'vehicle.licensePlate',
          label: this.$t('global.object.licensePlate'),
          isSortable: true,
          isRedirectable: true,
          thStyle: 'width: 350px',
        },
        {
          key: 'status',
          label: this.$t('master.list.driver.status'),
          isSortable: true,
          thStyle: 'width: 350px',
        },
        {
          key: 'route.name',
          label: this.$t('master.rute'),
          isSortable: true,
          thStyle: 'width: 350px',
        },
        {
          key: 'expectedStartOn',
          label: this.$t('route.routePlaning.list.scheduleStart'),
          isSortable: true,
          thStyle: 'width: 350px',
        },
        {
          key: 'expectedEndOn',
          label: this.$t('route.routePlaning.list.scheduleEnd'),
          isSortable: true,
          thStyle: 'width: 350px',
        },
      ]
    },

    query() {
      return {
        get: this.get,
      }
    },

    changeStatusList() {
      if (this.status == 'scheduled') {
        return [
          {
            id: 'progress',
            label: this.$t('master.status.dalamPerjalanan'),
          },
          {
            id: 'done',
            label: this.$t('master.status.selesai'),
          },
          {
            id: 'cancelled',
            label: this.$t('master.status.dibatalkan'),
          },
        ]
      } else if (this.status == 'progress') {
        return [
          {
            id: 'done',
            label: this.$t('master.status.selesai'),
          },
          {
            id: 'cancelled',
            label: this.$t('master.status.dibatalkan'),
          },
        ]
      }

      return null
    },
  },

  mounted() {
    this.refresh()
    this.track('View List Route Planning')
  },

  methods: {
    async get(filter) {
      this.filter = filter
      if (this.status) {
        this.filter = {
          ...this.filter,
          status: this.status,
        }
      }

      if (this.status === 'done' || this.status === 'cancelled') {
        this.hasCheckbox = false
      } else {
        this.hasCheckbox = true
      }

      const res = await this.$apollo.query({
        fetchPolicy: 'no-cache',
        query: GET_ROUTE_RECORDS,
        variables: {
          filter: this.filter,
        },
      })

      if (!res.data.routeRecords.routeRecords.data.length && this.filter.search == '') {
        this.empty = true
        return []
      }
      this.empty = false
      return res.data.routeRecords.routeRecords
    },

    filterByStatus(status) {
      this.empty = false
      if (this.status == status) {
        this.status = 'scheduled'
        this.refresh()
        return
      }

      this.status = status
      this.refresh()
    },

    refresh() {
      this.refreshData = true
      setTimeout(() => {
        this.refreshData = false
      }, 500)
    },

    updateTotal(total) {
      this.selectedIdsLength = total
    },

    openModalChangeStatus(status) {
      this.statusChanged = status

      this.$bvModal.show('mc-modal-change-status')
    },

    async changeStatus() {
      const _selected = Object.keys(this.$refs.tablePlanning.selectedIds).filter(
        (key) => this.$refs.tablePlanning.selectedIds[key] == true
      )
      const promises = []
      const self = this

      _selected.forEach((el) => {
        promises.push(
          self.$apollo.mutate({
            mutation: UPDATE_STATUS_ROUTE_RECORD,
            variables: {
              input: {
                routeRecordId: parseInt(el),
                oldData: {
                  status: self.status,
                },
                newData: {
                  status: self.statusChanged.id,
                },
              },
            },
          })
        )
      })

      await Promise.all(promises).then((res) => {
        const successStatus = res[0].data.updateRouteRecord.isSuccessful
        const message = res[0].data.updateRouteRecord.message

        this.$bvModal.hide('mc-modal-change-status')
        this.refreshData = true
        setTimeout(() => {
          this.refreshData = false
        }, 500)

        if (successStatus) {
          const statusMap = {
            progress: 'Dalam Perjalanan',
            done: 'Selesai',
            cancelled: 'Dibatalkan',
          }
          const valueChangeStatus = statusMap[self.statusChanged.id] || 'Unknown'
          this.track(`Change Status "${valueChangeStatus}" on Route Planning`)
          this.$mcToast.success(this.$t('global.alert.text.successUpdate'))
        } else {
          const collectionMessage = [
            {
              message: 'on its way',
              valueMessage: this.$t('global.alert.text.onItsWay'),
            },
            {
              message: 'scheduled route planning cannot be ended',
              valueMessage: this.$t('global.alert.text.scheduledRouteEnded'),
            },
          ]

          const matchedMessage = collectionMessage.find((item) =>
            message.includes(item.message)
          )?.valueMessage
          this.$mcToast.warning(matchedMessage || message)
        }
      })
    },
  },
}
</script>
